<ng-template #assignedToPopover>
  @for (username of assignedToUsers; track username) {
    <span class="u_block">{{ username }}</span>
  }
</ng-template>

<main>
  <rv-draggable [draggableService]="draggableService">
    <div resizeObserver (resizeEvent)="onHeaderResize($event)">
      @if (error()) {
        <div
          id="errorBox"
          class="madero-style alert alert-danger text-center u_margin-md-top"
          role="alert"
        >
          <p>
            <strong>{{ error().errorMessage }}</strong>
          </p>
          {{ error().apiError }}
        </div>
      }
      <div
        class="tw-flex tw-flex-col md:tw-items-center md:!tw-flex-row tw-py-5 tw-px-[15px] tw-gap-2.5"
      >
        <search-filter
          #searchFilter
          class="tw-w-full"
          [filterConfig]="filterConfig"
          [search]="searchState.getSearchObject()"
          [doSearch]="doSearch"
          [focus]="true"
        />
        @if (canUseFolders()) {
          <new-folder-button
            [parentId]="folderId"
            [existingFiles]="folders"
            kind="Schedule"
            (folderAdded)="listService.reload()"
          ></new-folder-button>
        }
        <button
          rv-button
          type="button"
          id="scheduleAddButton"
          class="tw-min-w-[200px] tw-whitespace-nowrap"
          uiSref="apps.schedules.add"
          [uiParams]="{ folderId }"
          *requireAccess="'schedule:fullAccess'"
        >
          <mat-icon svgIcon="add" class="bold" />&nbsp; Add Schedule
        </button>
      </div>
      <batch-actions styles="xl:!tw-mr-72 xl:!tw-min-w-[725px]" />
      <div
        class="tw-flex tw-flex-col tw-gap-2.5 tw-px-4 tw-border-solid tw-border-0 tw-border-t tw-border-gray-200"
      >
        <folder-path
          [folderTree]="folderService.folderPathTree"
          [folderId]="folderId"
          (folderClicked)="onFolderClicked($event)"
          rootName="Schedules"
        ></folder-path>
      </div>
    </div>
    <rv-divider />
    <div
      class="tw-px-2 schedules-list-container"
      #schedulesListContainer
      rvSpinner="schedules-list-loader"
      [rvShowSpinner]="loadingItems()"
    >
      <cdk-virtual-scroll-viewport
        #scrollViewport
        itemSize="68"
        class="scrollable-list horizontal-scroll rounded-rows"
      >
        <table
          id="schedulesListTable"
          class="table table--schedule-list"
          aria-label="Schedules List"
        >
          <thead class="table-header">
            <tr class="table-header__row u_clickable">
              <th
                class="table-header__cell col-sm-2 table-header__cell_{{
                  field.name
                }}"
                *ngFor="let field of tableFields | filterBy: 'renderIf' : true"
                id="tableHeader-{{ field.name }}"
                [ariaSort]="field.name"
                [sortBy]="searchState.getSearchObject().sortBy"
                [reverse]="searchState.getSearchObject().reverse"
              >
                <div
                  [class.flex-row]="field.name === 'name'"
                  [class.fixed-width]="field.name === 'name'"
                >
                  @if (field.name === 'name') {
                    <mat-checkbox
                      id="schedules-select-all-cb"
                      (click)="$event.stopPropagation()"
                      (change)="toggleSelection()"
                      [checked]="listState.isAllSelected()"
                      [aria-label]="
                        listState.isAllSelected()
                          ? 'Deselect all'
                          : 'Select all'
                      "
                      [aria-describedby]="
                        'tableHeader-' + field.name + '-label'
                      "
                      [ariaChecked]="listState.isAllSelected()"
                      ariaCheckedElementId="schedules-select-all-cb-input"
                      *requireRole="'!ap'"
                      class="mr-2"
                      matTooltip="Bulk select"
                      matTooltipShowDelay="500"
                      matTooltipClass="menu-tooltip"
                    />
                  }
                  <div
                    id="tableHeader-{{ field.name }}-label"
                    class="u_clickable table-header-label"
                    role="button"
                    tabindex="0"
                    (keyup.enter)="sort(field)"
                    (keydown.space)="$event.preventDefault(); sort(field)"
                    (click)="sort(field)"
                  >
                    {{ field.label }}
                    @if (searchState.getSearchObject().sortBy === field.name) {
                      <i
                        [attr.aria-label]="
                          searchState.getSearchObject().reverse
                            ? 'Descending order'
                            : 'Ascending order'
                        "
                        class="fa"
                        [ngClass]="{
                          'fa-caret-up': !searchState.getSearchObject().reverse,
                          'fa-caret-down':
                            searchState.getSearchObject().reverse,
                        }"
                      ></i>
                    }
                  </div>
                </div>
              </th>
              <th
                class="table-header__cell action-column"
                *requireAccess="'schedule:fullAccess'"
              ></th>
            </tr>
          </thead>
          <tbody class="table-body">
            @if (
              !(
                !loadingItems() &&
                list().length === 0 &&
                searchState.getSearchObject().query === undefined
              )
            ) {
              <ng-container
                *cdkVirtualFor="let listItem of list(); let idx = index"
              >
                @if (listItem.isFolder) {
                  <tr
                    uiSref="apps.schedules.list"
                    [uiParams]="{ folderId: listItem.item.id }"
                    [rvDraggableItem]="listItem.item"
                    [rvDroppableItem]="listItem.item"
                    class="table-body__row clickable"
                    [class.selected]="listItem.selected"
                  >
                    <td
                      class="table-body__cell display-name draggable-mirror-content"
                    >
                      <div class="tw-flex tw-flex-row tw-items-center tw-gap-4">
                        <mat-icon
                          rv-draggable-handle
                          class="draggable-mirror-content-hidden"
                          svgIcon="drag-stroke"
                        />
                        <mat-checkbox
                          id="schedules-cb-{{ idx }}"
                          class="draggable-mirror-content-hidden"
                          (click)="onCheckboxClick($event, listItem, idx)"
                          [checked]="listItem.selected"
                          [aria-label]="
                            listItem.selected
                              ? 'Deselect folder'
                              : 'Select folder'
                          "
                          [aria-describedby]="'folder-' + listItem.item.id"
                          [ariaChecked]="listItem.selected"
                          ariaCheckedElementId="schedules-cb-{{ idx }}-input"
                          *requireRole="'!ap'"
                          matTooltip="Bulk select"
                          matTooltipShowDelay="500"
                          matTooltipClass="menu-tooltip"
                        />
                        <div
                          class="tw-flex tw-flex-row tw-items-center tw-gap-2.5"
                        >
                          <mat-icon svgIcon="folder" class="tw-text-gray-500" />
                          <a
                            id="folder-{{ listItem.item.id }}"
                            uiSref="apps.schedules.list"
                            [uiParams]="{ folderId: listItem.item.id }"
                            (click)="$event.stopPropagation()"
                            class="madero-link tw-font-medium u_ellipsis-lg"
                          >
                            {{ listItem.item.name }}
                          </a>
                        </div>
                      </div>
                    </td>
                    <td>&nbsp;</td>
                    <td>&nbsp;</td>
                    @if (!userStateService.isAssignedPublisher()) {
                      <td>&nbsp;</td>
                    }
                    <td class="table-body__cell">
                      <change-details
                        [changeDate]="listItem.item.changeDate"
                        [changedBy]="listItem.item.changedBy"
                      />
                    </td>
                    @if (
                      companyStateService.isK12Customer() ||
                      companyStateService.isHigherEdCustomer()
                    ) {
                      <td>&nbsp;</td>
                    }
                    <td
                      class="table-body__cell action-column"
                      *requireAccess="'schedule:fullAccess'"
                    >
                      <operations-menu
                        [listItem]="listItem"
                        [operations]="folderOperations"
                        id="schedule-operations-menu-{{ idx + 1 }}"
                        (operationClick)="
                          handleOperationClick($event, listItem)
                        "
                      />
                    </td>
                  </tr>
                } @else {
                  <tr
                    [rvDraggableItem]="listItem.item"
                    uiSref="apps.schedules.details"
                    [uiParams]="{ scheduleId: listItem.item.id }"
                    class="table-body__row clickable"
                    [class.selected]="listItem.selected"
                  >
                    <td class="table-body__cell draggable-mirror-content">
                      <div class="tw-flex tw-flex-row tw-items-center tw-gap-4">
                        <mat-icon
                          rv-draggable-handle
                          class="draggable-mirror-content-hidden"
                          svgIcon="drag-stroke"
                        />
                        <mat-checkbox
                          id="schedules-cb-{{ idx }}"
                          class="draggable-mirror-content-hidden"
                          (click)="onCheckboxClick($event, listItem, idx)"
                          [checked]="listItem.selected"
                          [aria-label]="
                            listItem.selected
                              ? 'Deselect schedule'
                              : 'Select schedule'
                          "
                          [aria-describedby]="'schedule-' + listItem.item.id"
                          ariaCheckedElementId="schedules-cb-{{ idx }}-input"
                          [ariaChecked]="listItem.selected"
                          *requireRole="'!ap'"
                          matTooltip="Bulk select"
                          matTooltipShowDelay="500"
                          matTooltipClass="menu-tooltip"
                        />
                        <a
                          id="schedule-{{ listItem.item.id }}"
                          uiSref="apps.schedules.details"
                          [uiParams]="{ scheduleId: listItem.item.id }"
                          (click)="$event.stopPropagation()"
                          class="tw-font-medium u_ellipsis-lg"
                        >
                          {{ listItem.item.name }}
                        </a>
                      </div>
                    </td>
                    <td class="table-body__cell">
                      @if (listItem.item.scheduleTypeName) {
                        <schedule-type-pill
                          [scheduleTypeName]="listItem.item.scheduleTypeName"
                        ></schedule-type-pill>
                      }
                    </td>
                    @if (listItem.item.expired) {
                      <td class="table-body__cell tw-text-red-600">Expired</td>
                    } @else {
                      <td class="table-body__cell tw-text-gray-700">
                        <div class="tw-flex">
                          <span
                            class="u_ellipsis-lg"
                            [innerHtml]="listItem.item.timeline"
                          ></span>
                        </div>
                      </td>
                    }
                    @if (!userStateService.isAssignedPublisher()) {
                      <td class="table-body__cell">
                        @if (
                          listItem.item.distributionSummary ===
                          'No Display Selected'
                        ) {
                          <span class="tw-text-gray-400"> － </span>
                        } @else {
                          <rv-badge
                            [text]="listItem.item.distributionSummary"
                          />
                        }
                      </td>
                    }
                    <td class="table-body__cell u_ellipsis-md">
                      <change-details
                        [changeDate]="listItem.item.changeDate"
                        [changedBy]="listItem.item.changedBy"
                      />
                    </td>
                    @if (
                      companyStateService.isK12Customer() ||
                      companyStateService.isHigherEdCustomer()
                    ) {
                      <td class="table-body__cell">
                        <span class="u_ellipsis-md">
                          {{
                            listItem.item.assignedUsers &&
                            listItem.item.assignedUsers.length > 0
                              ? (listItem.item.assignedUsers[0] | username)
                              : ''
                          }}
                        </span>
                        @if (
                          listItem.item.assignedUsers &&
                          listItem.item.assignedUsers.length > 1
                        ) {
                          <rv-badge
                            class="u_clickable ml-2"
                            appearance="solid"
                            color="blue"
                            size="compact"
                            [text]="
                              '+' + (listItem.item.assignedUsers.length - 1)
                            "
                            [popover]="assignedToPopover"
                            (onShown)="popoverText(listItem.item.assignedUsers)"
                            containerClass="madero-style"
                            outsideClick="true"
                            placement="bottom right"
                          />
                        }
                      </td>
                    }
                    <td
                      class="table-body__cell action-column"
                      *requireAccess="'schedule:fullAccess'"
                    >
                      <operations-menu
                        [listItem]="listItem"
                        [operations]="listOperations.operations"
                        [entityId]="listItem.item.id"
                        [listItem]="listItem"
                        id="schedule-operations-menu-{{ idx + 1 }}"
                        (operationClick)="
                          handleOperationClick($event, listItem)
                        "
                      />
                    </td>
                  </tr>
                }
              </ng-container>
            }
            <!-- If no search results -->
            @if (!loadingItems() && list().length === 0) {
              <tr class="placeholder">
                <td colspan="6">
                  <div
                    class="tw-flex tw-flex-col tw-items-center tw-w-full tw-mb-[50px]"
                  >
                    @if (searchState.getSearchObject().query === undefined) {
                      <img
                        src="../images/empty-schedules.svg"
                        alt="Empty schedules"
                      />
                      @if (userStateService.isAssignedPublisher()) {
                        <h3>You have no Schedules Assigned to you</h3>
                      } @else {
                        <h3>Create your first schedule</h3>
                      }
                      @if (userStateService.isAssignedPublisher()) {
                        Please ask an admin to assign a schedule to you. You
                        will then be able to add presentations to it, and assign
                        it to a display.
                      } @else {
                        <p
                          class="tw-block tw-w-1/2 tw-text-center tw-text-gray-600 tw-text-wrap"
                        >
                          A schedule is a playlist of multiple presentations
                          with rules as to what days and times to play. Click
                          Add Schedule to create your first schedule.
                        </p>
                        <a
                          rv-button
                          variant="green-text"
                          id="emptySchedulesAddButton"
                          uiSref="apps.schedules.add"
                          *requireAccess="'schedule:fullAccess'"
                        >
                          Add Schedule
                        </a>
                      }
                    } @else {
                      <no-results (action)="searchFilter.reset()"></no-results>
                    }
                  </div>
                </td>
              </tr>
            }
          </tbody>
        </table>
      </cdk-virtual-scroll-viewport>
    </div>
  </rv-draggable>
</main>
